/**
 * File-transfer sender core — picker -> hash+OPFS-copy -> encrypt -> upload -> complete.
 *
 * Runs entirely in the side-panel PAGE context (never the background service
 * worker — the SW must not host long-running chunk-encrypt/upload work; see
 * the extension-mv3-gotchas skill). M2 (TASK-19): a cooperative pause/cancel
 * checkpoint (`SendControlDeps.checkInterrupt`, `uploadLoop.ts`) lets a local
 * user Pause/Cancel click interrupt the upload loop between parts — pause
 * throws `UploadPausedError` and settles as `{stage:"paused"}` (the sender
 * record stays resumable), cancel throws `UploadCancelledError` and settles
 * as `{stage:"failed", reason:"cancelled"}`. Closing the panel mid-send is
 * still a crash — `resumePendingSends()` (senderResume.ts) picks it back up
 * on the next panel mount.
 *
 * `sendFile` is deliberately pure of `browser.*`/`fetch`/crypto-library/OPFS
 * specifics — every side effect is injected via `SenderDeps` so this module
 * is fully unit-testable with fakes (see __tests__/sendFile.test.ts). The
 * REAL wiring (background-relayed ctrl messages, direct-fetch transfer API,
 * real @reflecto/core crypto, real OPFS + state persistence) lives in the
 * sibling `senderDeps.ext.ts` adapter, mirroring how `PairConfirmCardSlot`
 * calls `background/api.ts` helpers directly from panel code for calls that
 * don't need SW-owned state.
 *
 * Flow (docs/file-transfer-resume.md §3.1.4, §4.1, §8.4; docs/file-transfer-p2p.md §16 pt.4):
 *   1. Reject files over the M1 32 MiB cap BEFORE reading any bytes (M2
 *      keeps the cap — removing it is TASK-16 scope).
 *   2. Quota preflight (§3.1.4 step 1) — fail locally, no ctrl, if the OPFS
 *      write-through copy wouldn't fit.
 *   3. PASS 1 — single read of `file.stream()`: streaming SHA-256 AND
 *      write-through the plaintext to the OPFS copy (`deps.openSource`). No
 *      encryption happens in this pass (§3.1.4 point 2/3 — the M1 in-memory
 *      ciphertext buffering is gone; upload always re-reads the durable
 *      copy, which is what makes resume share this same upload code path).
 *   4. Build + validate the v1 manifest, send the "offer" ctrl, await
 *      accept. A resolved accept may carry `resumeFromChunk` (§8.4) —
 *      clamped to a `skipBelowPart` part index (M2 relay flows never
 *      actually see resumeFromChunk > 0; specified for completeness/M3).
 *   5. POST /v1/transfers, then P-S1 persist the full sender record
 *      (docs/file-transfer-resume.md §4.1) BEFORE any part upload.
 *   6. If `deps.p2p` is eligible (`FT_P2P_ENABLED`, `skipBelowPart === 0` —
 *      P2P only ever attempts on the initial live session, §1.6 — Web Lock
 *      held), attempt a P2P promotion BEFORE PASS 2 — one of three outcomes:
 *        - "not-attempted" (no `deps.p2p` / ineligible / `connect()` -> null):
 *          falls through to PASS 2 (step 7) unmodified, byte-identical M2
 *          behavior.
 *        - "demoted": persist the §8.3 `skipBelowPart` mapping (P-S5), then
 *          hand off DIRECTLY to `senderResume.ts`'s `reconcileAndContinue`
 *          and return early — steps 7-8 below never run for this send.
 *        - "completed": the P2P attempt already ran the receiver's whole-file
 *          verify in-band — skip PASS 2 entirely and fall straight into the
 *          completion tail (step 8).
 *   7. PASS 2 (`uploadLoop.ts`'s `runUploadLoop`, shared with resume):
 *      sequentially encrypt+upload each part `[skipBelowPart, partCount)`,
 *      reading plaintext from the OPFS copy; P-S2 persist after each part.
 *      Skipped entirely on a P2P "completed" outcome (step 6).
 *   8. POST .../complete, send the "complete" ctrl, P-S4 persist terminal
 *      "complete" + delete the OPFS copy. Shared verbatim by both the
 *      relay (step 7) and P2P-"completed" (step 6) paths.
 *
 * SECURITY: never log file name/content/manifest fields — security.md.
 */

import type { FileCtrl, FileEntry, FtErrorCode, TransferManifest } from "@reflecto/core";
import {
  AEAD_OVERHEAD,
  CHUNK_SIZE,
  CHUNKS_PER_PART,
  encodeBase64,
  FT_P2P_ENABLED,
  FT_PROTOCOL_VERSION,
  NONCE_PREFIX_LEN,
  validateManifest,
} from "@reflecto/core";
import type { SendControlDeps } from "./activeSendControl";
import { isAlreadyComplete, isDefinitive404, isQuotaExceeded } from "./errors";
import type { EngineFileLike } from "./fileSeam";
import { ftLog } from "./ftLog";
import {
  type FtTransport,
  mapDemoteToSkipBelowPart,
  type P2pSendOutcome,
  type SenderP2pDeps,
} from "./p2pTypes";
import { partPlan } from "./partPlan";
import {
  persistTerminal as persistTerminalSender,
  reconcileAndContinue as reconcileSenderAndContinue,
} from "./senderResume";
import type { FtSenderStateRecord } from "./state";
import {
  PART_RETRY_BACKOFF_MS,
  PartUploadError,
  runUploadLoop,
  SourceUnderYieldError,
  UploadCancelledError,
  UploadPausedError,
  UploadPeerFailedError,
} from "./uploadLoop";

// Re-exported so existing import paths (`import { PartUploadError, PART_RETRY_BACKOFF_MS } from
// "./sendFile"`, e.g. __tests__/sendFile.test.ts) keep working — the definitions moved to
// uploadLoop.ts to avoid a runtime circular import (see that module's doc).
export {
  PART_RETRY_BACKOFF_MS,
  PartUploadError,
  SourceUnderYieldError,
  UploadCancelledError,
  UploadPausedError,
  UploadPeerFailedError,
};

/**
 * The 4 GiB protocol ceiling, expressed as a part-count bound (TASK-16 — replaces the M1 32 MiB
 * `FT_M1_MAX_BYTES` cap): `ceil(4 GiB / (CHUNKS_PER_PART * CHUNK_CIPHER_SIZE))`. Mirrors the
 * server's `FT_MAX_PART_COUNT` (`apps/server/src/routes/transfers.schema.ts`) and Android's
 * `FtConstants.MAX_PART_COUNT` — three independent local derivations of the same value from the
 * same `@reflecto/core` primitives, not a single shared constant (the old cap used the same
 * "deliberately independent" pattern).
 */
export const FT_MAX_PART_COUNT = Math.ceil(
  (4 * 1024 ** 3) / (CHUNKS_PER_PART * (CHUNK_SIZE + AEAD_OVERHEAD))
);

/**
 * Plaintext byte bound implied by [FT_MAX_PART_COUNT] (`chunkCount <= FT_MAX_PART_COUNT *
 * CHUNKS_PER_PART`, full-size chunks) — advisory only, for the UI's early "too large" indicator
 * (`SendFileCard`'s `capabilities.maxBytes`). The AUTHORITATIVE check inside [sendFile] is the
 * partCount comparison above, computed from the file's real chunk count.
 */
export const FT_MAX_BYTES = FT_MAX_PART_COUNT * CHUNKS_PER_PART * CHUNK_SIZE;

/** Client-side "no answer from phone" timeout. Server-side offer TTL is a separate, longer 1h (D6). */
export const ACCEPT_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Accept window when the recipient is on the **foreground tier** — no FCM token, so it cannot be
 * woken and only sees the offer once someone opens Reflecto on it.
 *
 * Five minutes assumes a push that arrives in seconds. Against a phone that must be picked up
 * first it is a guaranteed `no-answer`, which is why the push-fallback spec's D5 calls for the
 * sender to relax this rather than fail.
 *
 * 30 minutes, not longer, and the ceiling is ours rather than the server's: the server transfer
 * record lives 12h (`FT_TTL_SECONDS`) so it is nowhere near binding, but the sender holds the
 * staged OPFS bytes for the whole wait and prunes staged records at 1h. Waiting past our own
 * bytes' lifetime would replace a clean timeout with a confusing failure later on.
 */
export const FOREGROUND_ACCEPT_TIMEOUT_MS = 30 * 60 * 1000;

export class FileTooLargeError extends Error {
  constructor() {
    super(`file exceeds the ${FT_MAX_PART_COUNT}-part / 4 GiB protocol ceiling`);
    this.name = "FileTooLargeError";
  }
}

/** Thrown when the OPFS write-through copy preflight/write fails (§3.1.4 step 1/2) — local UI error, no ctrl ever sent. */
export class InsufficientStorageError extends Error {
  constructor() {
    super("not enough local storage for the file-transfer write-through copy");
    this.name = "InsufficientStorageError";
  }
}

export class OfferDeclinedError extends Error {
  constructor() {
    super("recipient declined the file offer");
    this.name = "OfferDeclinedError";
  }
}

export class OfferNoAnswerError extends Error {
  constructor() {
    super("no answer from phone within the accept window");
    this.name = "OfferNoAnswerError";
  }
}

export class InvalidManifestError extends Error {
  constructor(reason: string) {
    super(`built an invalid manifest: ${reason}`);
    this.name = "InvalidManifestError";
  }
}

/** The two chunk-crypto primitives from @reflecto/core, injected for testability (see the module doc). */
export interface SenderCrypto {
  deriveTransferKey(
    sharedSecret: Uint8Array,
    transferId: string,
    senderPub: Uint8Array,
    recipientPub: Uint8Array
  ): Promise<Uint8Array>;
  encryptChunk(
    key: Uint8Array,
    noncePrefix: Uint8Array,
    ids: { transferId: string; fileId: string },
    /** File-LOCAL chunk index — the AAD's `chunkIndex` (crypto-spec.md §6). */
    chunkIndex: number,
    isFinal: boolean,
    plaintext: Uint8Array,
    /**
     * File-aligned GLOBAL chunk index — the NONCE counter (crypto-spec.md §6a).
     * Required here, unlike on the `@reflecto/core` primitive (where it defaults to
     * `chunkIndex` for the single-file callers), because the engine is multi-file and
     * must never let a per-file counter reuse a (key, nonce) pair across files.
     */
    nonceChunkIndex: number
  ): Promise<Uint8Array>;
}

/**
 * Streaming SHA-256, split into init/update/final so the manifest's
 * whole-file hash can be computed in the SAME read pass as the OPFS
 * write-through copy (see @reflecto/core's sha256Init/sha256Update/sha256Final —
 * Web Crypto's `crypto.subtle.digest` has no incremental API, so this can't
 * be done with a browser built-in alone).
 */
export interface SenderHash {
  init(): unknown | Promise<unknown>;
  update(state: unknown, chunk: Uint8Array): void;
  final(state: unknown): Uint8Array;
}

export interface TransferApi {
  createTransfer(args: {
    transferId: string;
    recipientDeviceId: string;
    partCount: number;
    cipherBytes: number;
  }): Promise<{ expiresAt: number }>;
  uploadUrl(transferId: string, partIndex: number): Promise<{ url: string; expiresAt: number }>;
  putPart(url: string, body: Uint8Array): Promise<void>;
  markUploaded(transferId: string, partIndex: number): Promise<void>;
  complete(transferId: string): Promise<void>;
  deleteTransfer(transferId: string): Promise<void>;
}

/**
 * The write-through OPFS source copy (docs/file-transfer-resume.md §3.1.4).
 * One instance is scoped to a single transfer (the real adapter's factory
 * binds `transferId`/`chunkSize`/the file's total size at construction).
 */
export interface SenderSource {
  /**
   * Preflight (§3.1.4 step 1): `navigator.storage.estimate()` against
   * `fileSize + FT_OPFS_QUOTA_MARGIN_BYTES`. Returns `false` when there
   * isn't enough room — callers fail locally and send no ctrl (no offer
   * exists yet).
   */
  checkQuota(fileSize: number): Promise<boolean>;
  /**
   * Append one plaintext chunk to the write-through copy during the hash
   * pass (§3.1.4 step 2), in ascending chunk-index order. Any failure
   * (including a `QuotaExceededError` mid-write) is the caller's cue to
   * delete the partial copy and fail locally.
   */
  appendChunk(chunk: Uint8Array): Promise<void>;
  /**
   * Read plaintext chunks `[startIndex, endIndexInclusive]` from the
   * DURABLE copy — never the original `File` (§3.1.4 point 3, §5.2 step 5).
   * The same call shape serves a fresh send's pass 2 (right after the hash
   * pass) and a resumed send (after a crash) — one code path.
   */
  readChunks(
    startIndex: number,
    endIndexInclusive: number
  ): AsyncGenerator<{ index: number; data: Uint8Array }>;
  /** Delete the write-through copy — hash-pass failure, or terminal cleanup (P-S4). */
  deleteCopy(): Promise<void>;
  /**
   * Current byte size of the durable copy, or `null` if it doesn't exist.
   * Used by the resume algorithm's source check (§5.2 step 1: the OPFS copy
   * must exist with `size === manifest.files[0].size`) — not needed by a
   * fresh send.
   */
  getSize(): Promise<number | null>;
}

/** Persists the sender resume record (docs/file-transfer-resume.md §4.1 P-S1/P-S2/P-S4). */
export interface SenderStateWriter {
  save(record: FtSenderStateRecord): Promise<void>;
}

/**
 * The receiver's decision on our offer (docs/file-transfer-resume.md §8.4 —
 * widens the M1 bare `"accept"|"decline"` string to carry `resumeFromChunk`
 * off an `accept` ctrl). `resumeFromChunk` is defensively clamped by
 * `sendFile`/`senderResume` to a part boundary — never trusted as-is.
 */
export type AcceptDecision =
  | { decision: "accept"; resumeFromChunk?: number }
  | { decision: "decline" };

export type SendFailReason =
  | "too-large"
  | "storage-full"
  | "rejected"
  | "no-answer"
  | "upload-failed"
  | "cancelled";

/**
 * WHERE a send died, as opposed to WHY ([SendFailReason]) or with what wire code
 * (`FtErrorCode`). Threaded as a real field rather than reconstructed at the
 * capture site, because neither of the other two can separate cases that need
 * separating: `storage-full` fires both at the pre-flight quota check and again
 * mid-transfer, and `upload-failed`/`INTERNAL` covers retry exhaustion, the
 * create call, and the final complete step alike. Reconstructing per surface
 * would also let the values drift between extension, web and Android.
 *
 * Mirrors the `stage` enum in `packages/core/analytics/events.json` — these are
 * stable analytics wire values, so treat them as append-only.
 */
export type FtFailStage =
  /** Before anything left this device: size check, quota pre-flight, OPFS staging. */
  | "preflight"
  /** Offer sent, awaiting the peer's accept/decline — includes no-answer and rejection. */
  | "offer"
  /** Bytes in flight: create, part upload, retry exhaustion, cancel mid-stream. */
  | "transfer"
  /**
   * Whole-file hash verification failed. Produced on BOTH roles: the receiver fails
   * its own verify, and the sender learns about it via the peer's terminal
   * `error{HASH_MISMATCH}` ctrl (the `UploadPeerFailedError` branch below, #841).
   * Do NOT use it for a mid-flight death — that is `"transfer"`; this value means
   * the bytes all arrived and did not match.
   */
  | "verify"
  /** All bytes present and verified; failed handing off to downloads/storage. */
  | "finalize"
  /** Terminal reached from a paused record, with no live loop running. */
  | "paused";
// NOTE: there is deliberately no "unknown" member. `failStage` is REQUIRED on the
// failed variant precisely so every new failure path must decide; an "unknown" escape
// hatch would let a call site opt out of that decision, and nothing has ever produced
// it. Removing it is safe with no history to split — it has never been emitted.

export type SendProgressEvent =
  /**
   * Pass-1 "Preparing" progress (issue #808 item 1). Emitted once at 0 before the read
   * loop, then on each integer-percent increase, so the send card can show "Preparing — N%"
   * during the offer-gating hash+stage pass instead of dead air. `totalBytes` is the file
   * size (0 for an empty file — the UI treats that as 100%).
   */
  | { stage: "hashing"; bytesHashed: number; totalBytes: number }
  /**
   * Offer sent, no delivery receipt yet (#808 item 6). FCM/Web-Push wake-up can
   * lag noticeably (minutes on a dozing phone), so this is a distinct stage from
   * `"waiting-accept"`: the card shows "Delivering…" until the recipient's
   * `offer_received` ack flips it to `"waiting-accept"`. An older recipient that
   * never sends the ack simply leaves the card here until accept/decline.
   */
  | { stage: "delivering"; transferId: string }
  /**
   * `needsAppOpen` marks the foreground-tier wait: the recipient has no push token, so nothing
   * will surface this offer until someone opens Reflecto on that phone. Surfaces should say so
   * ("ask them to open Reflecto") rather than showing an ordinary "waiting" spinner, which reads
   * as "any second now" for what may be a much longer wait.
   *
   * Deliberately a field on the existing stage rather than a new stage: a surface that has not
   * been updated still renders a correct — if less helpful — waiting state, instead of falling
   * through an unhandled `switch` arm.
   */
  | { stage: "waiting-accept"; transferId: string; needsAppOpen?: boolean }
  /**
   * `transport` (TASK-30) is set to `"p2p"` ONLY for the one synthetic event `sendFile.ts` emits the
   * instant a P2P attempt's data channel opens (TRANSFERRING_P2P) — omitted (relay upload, the
   * common case) otherwise. The UI's "direct" badge shows exactly while the most recent `"uploading"`
   * event carries `transport === "p2p"`; a subsequent relay-fallback `"uploading"` event (post-demotion)
   * naturally clears it by omitting the field, and any other stage (`paused`/`done`/`failed`) clears
   * it structurally since the badge is only ever read off an `"uploading"` state.
   */
  | {
      stage: "uploading";
      partIndex: number;
      partCount: number;
      transferId: string;
      transport?: "p2p";
    }
  /** Local (non-terminal) pause — the record persisted "paused"; the UI should offer a Resume/Cancel affordance. Never fired for the "too-large"/quota-preflight paths (no record exists yet there). */
  | { stage: "paused"; transferId: string }
  /**
   * Terminal success. `transport` mirrors the receiver's `onTerminal` 4th arg so
   * `file_transfer_completed` can split P2P from relay on BOTH halves of a transfer —
   * a receiver-only split would answer "did P2P work?" for exactly half the population.
   */
  | { stage: "done"; transferId: string; transport: FtTransport }
  /**
   * `transferId` is omitted only for the pre-transferId "too-large" rejection — every other failure
   * path has one. `errorCode` (TASK-30) is set ONLY for the subset of `SendFailReason`s that
   * correspond to a wire `FtErrorCode` (`storage-full`→STORAGE_FULL, and `upload-failed`'s three
   * sub-causes EXPIRED/QUOTA_EXCEEDED/INTERNAL) — `too-large`/`rejected`/`no-answer`/`cancelled` are
   * UI-only outcomes with no protocol error code and never set it. The UI maps `errorCode` (when
   * present) to a code-specific string; otherwise it falls back to `reason`'s existing UI-only text.
   */
  | {
      stage: "failed";
      reason: SendFailReason;
      transferId?: string;
      errorCode?: FtErrorCode;
      /** WHERE it died — see [FtFailStage]. Required so a new failure path can't be added without deciding. */
      failStage: FtFailStage;
    }
  /**
   * The RECEIVER's inbound `progress` ctrl (§8.2 "UI only" row) — the sender
   * never sends its own progress ctrl, only displays the peer's, monotonically
   * clamped (`activeSendControl.ts`'s `clampMonotonicProgress`) so a stale
   * replay can never regress the shown value.
   */
  | { stage: "peer-progress"; transferId: string; chunksDone: number };

export interface SenderDeps {
  api: TransferApi;
  /**
   * Whether the recipient can be woken by push. **Defaults to `true`** when omitted, so every
   * existing caller keeps today's behaviour exactly.
   *
   * `false` means the foreground tier: the offer waits until someone opens Reflecto on that
   * phone, so the accept window relaxes to [FOREGROUND_ACCEPT_TIMEOUT_MS] and the wait is
   * reported with `needsAppOpen`. The host supplies this — it already tracks the tier for the
   * reply/action gating shipped in slice 3 — rather than this module fetching it, which would
   * put a network call inside the engine.
   */
  targetPushCapable?: boolean;
  /** Encrypt-then-POST a file_ctrl envelope to the recipient (background/fileCtrl.ts's sendFileCtrl, relayed via the SW). */
  sendCtrl(ctrl: FileCtrl): Promise<void>;
  /**
   * Resolves once the corresponding accept/decline ctrl arrives for this
   * transferId. MUST resolve exactly once per transferId even if a duplicate
   * ctrl arrives (SSE replay) — that idempotency lives in the adapter, not
   * here (see senderDeps.ext.ts). Rejects on the accept-window timeout —
   * `sendFile` maps a rejection to the "no-answer" failure reason and a
   * resolved decline to the "rejected" reason.
   *
   * `onOfferReceived` (#808 item 6) is invoked at most once, if the recipient's
   * `offer_received` delivery-receipt ctrl arrives while still awaiting the
   * accept/decline — the adapter fires it (from the same `notifyFileCtrlReceived`
   * seam that resolves this promise) so `sendFile` can advance the card from
   * "delivering" to "waiting-accept". Never called after the promise settles;
   * an adapter that doesn't implement it just leaves the card on "delivering".
   */
  awaitAccept(
    transferId: string,
    timeoutMs: number,
    onOfferReceived?: () => void
  ): Promise<AcceptDecision>;
  crypto: SenderCrypto;
  hash: SenderHash;
  /**
   * Open the OPFS write-through copy for `transferId`'s file at `fileIndex` (§3.1.4;
   * multi-file transfer design §5 — one write-through copy per file, index into
   * the eventual `manifest.files`). A factory (not a pre-bound instance) because
   * `sendFile` generates `transferId` internally — the real adapter can't know it
   * in advance. The RESUME loop calls the same factory with the record's existing
   * `transferId` and each of `manifest.files`'s indices.
   */
  openSource(transferId: string, fileIndex: number): SenderSource;
  /** Sender resume-state persistence (§4.1). */
  state: SenderStateWriter;
  /** Raw X25519 shared secret (crypto_scalarmult output) — see deriveSharedSecret in @reflecto/core. */
  sharedSecret: Uint8Array;
  senderDeviceId: string;
  senderPublicKey: Uint8Array;
  recipientPublicKey: Uint8Array;
  onProgress?(event: SendProgressEvent): void;
  /**
   * Pause/cancel/peer-progress control seam (TASK-19; real implementation:
   * `activeSendControl.ts`'s `activeSendControl` singleton). Optional — a
   * caller that omits it gets no pause/cancel/peer-progress behavior (the
   * upload loop just never checks for an interrupt), which is exactly the
   * pre-TASK-19 M1/M2 behavior every existing test relies on.
   */
  control?: SendControlDeps;
  /**
   * P2P promotion seam (docs/file-transfer-p2p.md §16 pt.4) — optional; ABSENT means byte-identical
   * M2 behavior (every existing test still passes unmodified). Even when present, `sendFile`
   * attempts P2P only when `FT_P2P_ENABLED` and `skipBelowPart === 0` (§3) — this function is
   * always the fresh/initial-live-session path, so that gate structurally satisfies "P2P only on
   * the initial live session" (§1.6): `SenderResumeDeps` never gains a `p2p` field, so a resume can
   * never re-promote.
   */
  p2p?: SenderP2pDeps;
  /** Injectable for deterministic tests; default to the real Web Crypto/Date globals. */
  randomUUID?(): string;
  randomNoncePrefix?(): Uint8Array;
  now?(): number;
}

function bytesToHex(bytes: Uint8Array): string {
  let hex = "";
  for (const b of bytes) hex += b.toString(16).padStart(2, "0");
  return hex;
}

/** Pass-1 write-slab size — coalesces stream reads before each awaited OPFS write (issue #808 item 1). */
const PASS1_SLAB_BYTES = 8 * 1024 * 1024;

/**
 * Clamp `accept.resumeFromChunk` to a `skipBelowPart` part index
 * (docs/file-transfer-resume.md §8.4 consumption rule) — defensive, trusts
 * nothing off the wire. `undefined`/`0` both mean "nothing to skip".
 */
export function clampSkipBelowPart(
  resumeFromChunk: number | undefined,
  chunkCount: number
): number {
  if (!resumeFromChunk || resumeFromChunk <= 0) return 0;
  const k = Math.min(
    Math.max(0, Math.floor(resumeFromChunk / CHUNKS_PER_PART) * CHUNKS_PER_PART),
    chunkCount
  );
  // Math.floor is an extra defensive guard beyond the spec's literal
  // formula: k is a CHUNKS_PER_PART multiple in every ordinary case, but
  // the outer `min(..., chunkCount)` clamp can land on a non-multiple when
  // chunkCount itself isn't one (the completed-output edge case, §6.2 step
  // 3) — flooring here just means that residual final part gets
  // (harmlessly) re-uploaded rather than the record ending up with a
  // non-integer skipBelowPart.
  return Math.floor(k / CHUNKS_PER_PART);
}

/**
 * Offer `file` to `recipientDeviceId` and, once accepted, encrypt-then-upload
 * it through the blind relay contract. Thin single-file wrapper over
 * [sendFiles] — see that function's doc for the full flow.
 */
export async function sendFile(
  file: EngineFileLike,
  recipientDeviceId: string,
  deps: SenderDeps
): Promise<void> {
  return sendFiles([file], recipientDeviceId, deps);
}

/**
 * Offer `files` (1..N) to `recipientDeviceId` as ONE transfer and, once accepted,
 * encrypt-then-upload them through the blind relay contract (multi-file transfer
 * design, docs/superpowers/specs/2026-07-26-multi-file-transfer-design.md §5). See
 * the module doc for the full single-file flow — this is its N-file generalization;
 * `deps` supplies every side effect (network, crypto, ctrl messaging, OPFS, state
 * persistence).
 *
 * P2P (docs/file-transfer-p2p.md) is gated to `files.length === 1` this slice — the
 * P2P chunk loop lives per-surface (extension/web `p2pSenderAttempt.ts`) and still
 * speaks single-file global chunk indexing; multi-file P2P (design doc §4a) is a
 * later per-surface slice. A multi-file send always uses the relay path below,
 * `partPlan`-driven (design doc §4): a global part belongs to exactly one file, so
 * parts never straddle a file boundary.
 */
export async function sendFiles(
  files: EngineFileLike[],
  recipientDeviceId: string,
  deps: SenderDeps
): Promise<void> {
  const notify = (event: SendProgressEvent) => deps.onProgress?.(event);
  const randomUUID = deps.randomUUID ?? (() => crypto.randomUUID());
  const randomNoncePrefix =
    deps.randomNoncePrefix ?? (() => crypto.getRandomValues(new Uint8Array(NONCE_PREFIX_LEN)));
  const now = deps.now ?? Date.now;

  // Part-count math (from file SIZES alone — no bytes touched) BEFORE anything else, so
  // the 4 GiB protocol ceiling check below never allocates/reads/quota-checks a rejected
  // file. `chunkCount` is fully determined by `size` (ceil(size / CHUNK_SIZE), 0-byte -> 1
  // empty final chunk) — computed once here and reused verbatim as each FileEntry's
  // `chunkCount` after PASS 1 below (never recomputed from post-hash state).
  const sizes = files.map((f) => f.size);
  const chunkCounts = sizes.map((size) => (size === 0 ? 1 : Math.ceil(size / CHUNK_SIZE)));
  const totalBytes = sizes.reduce((sum, s) => sum + s, 0);
  // `partPlan` only needs a `fileId` per entry to echo back in its output — the real ids
  // aren't minted until PASS 1 mints them per file below, so a positional placeholder is
  // used here purely to get `.length`; never persisted or logged. Degenerate single-file
  // case: identical to the old `Math.ceil(chunkCount / CHUNKS_PER_PART)`.
  const precheckPartCount = partPlan(
    chunkCounts.map((chunkCount, i) => ({ fileId: String(i), chunkCount }))
  ).length;

  if (precheckPartCount > FT_MAX_PART_COUNT) {
    notify({ stage: "failed", reason: "too-large", failStage: "preflight" });
    throw new FileTooLargeError();
  }

  notify({ stage: "hashing", bytesHashed: 0, totalBytes });

  const transferId = randomUUID();
  const noncePrefixBytes = randomNoncePrefix();

  const transferKey = await deps.crypto.deriveTransferKey(
    deps.sharedSecret,
    transferId,
    deps.senderPublicKey,
    deps.recipientPublicKey
  );

  const sources: SenderSource[] = [];
  const builtFiles: FileEntry[] = [];
  const deleteAllSources = async (): Promise<void> => {
    await Promise.all(sources.map((s) => s.deleteCopy().catch(() => {})));
  };

  // Aggregate PASS-1 hashing progress ACROSS every file, against the transfer's
  // totalBytes — a single-file transfer's totalBytes === file.size, so this is
  // byte-identical to the old per-file percent math.
  let hashedBytesGrand = 0;
  let lastHashPctGrand = 0;

  // PASS 1: for each file (in manifest order), a single read of file.stream() — hash AND
  // write-through the plaintext copy. No encryption here (§3.1.4 point 2/3).
  //
  // Hardware-demo perf finding (issue #808 item 1): this pass gates the OFFER,
  // so its speed is the "how long until the phone hears anything" latency.
  // Chunk boundaries are irrelevant here (they only matter for pass 2's
  // encrypt/frame step; SHA-256 and the byte-stream copy are boundary-
  // agnostic), so consume the stream's native reads directly and coalesce
  // writes into large slabs — per-64KB awaited OPFS write IPCs were the
  // dominant cost (~16k round-trips/GiB; slabs cut that ~128x).
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const size = sizes[i];
    const chunkCount = chunkCounts[i];
    const source = deps.openSource(transferId, i);
    sources.push(source);

    // §3.1.4 step 1 — preflight BEFORE any write for THIS file; a failure here means no
    // bytes from this file (or any not-yet-opened later file) were ever written, but an
    // earlier file in the loop may already have a durable copy — clean up ALL of them.
    const hasSpace = await source.checkQuota(size);
    if (!hasSpace) {
      await deleteAllSources();
      notify({
        stage: "failed",
        reason: "storage-full",
        errorCode: "STORAGE_FULL",
        failStage: "preflight",
      });
      throw new InsufficientStorageError();
    }

    const hashState = await deps.hash.init();
    try {
      const reader = file.stream().getReader();
      let slab: Uint8Array[] = [];
      let slabBytes = 0;
      const flushSlab = async (): Promise<void> => {
        if (slabBytes === 0) return;
        const joined = new Uint8Array(slabBytes);
        let offset = 0;
        for (const piece of slab) {
          joined.set(piece, offset);
          offset += piece.byteLength;
        }
        slab = [];
        slabBytes = 0;
        await source.appendChunk(joined);
      };
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          deps.hash.update(hashState, value);
          hashedBytesGrand += value.byteLength;
          slab.push(value);
          slabBytes += value.byteLength;
          if (slabBytes >= PASS1_SLAB_BYTES) await flushSlab();
          // Throttle "Preparing — N%" to integer-percent increases (<=100 events/transfer,
          // regardless of file size or file count) so the card re-renders smoothly, not
          // per stream read. Percent is against the TRANSFER's totalBytes (all files), not
          // this file's own size — for a single file totalBytes === file.size, so this is
          // byte-identical to the old per-file percent math.
          const pct = totalBytes === 0 ? 100 : Math.floor((hashedBytesGrand / totalBytes) * 100);
          if (pct > lastHashPctGrand) {
            lastHashPctGrand = pct;
            notify({ stage: "hashing", bytesHashed: hashedBytesGrand, totalBytes });
          }
        }
        await flushSlab();
        // 0-byte file: the stream yields nothing, so no appendChunk ran and the
        // OPFS copy was never created — force-create it (the old fixed-size
        // rechunker guaranteed "one empty final chunk" for exactly this reason;
        // pass 2's strict chunkCount contract treats size 0 as 1 empty chunk).
        if (size === 0) await source.appendChunk(new Uint8Array(0));
      } finally {
        reader.releaseLock();
      }
    } catch (err) {
      // Audit fix (#818 Part 4 sweep): this catch spans the WHOLE of this file's pass 1 —
      // stream reads, hashing, and every OPFS `appendChunk` — so a revoked file handle, a
      // read error, or a worker fault used to be reported to the user as
      // "storage-full" with the cause discarded unbound. Quota genuinely maps to
      // STORAGE_FULL; everything else keeps the same terminal shape (the caller
      // contract is unchanged) but now carries its real identity in the log.
      // Only QuotaExceededError is quota — matching `isQuotaExceededError` in receiveHost.ts /
      // receiveResume.ts. `NotAllowedError` is a PERMISSION failure per the File System Access
      // spec (#828 review): folding it in here would reintroduce, in the log, exactly the
      // misdiagnosis this change exists to remove.
      const isQuota = err instanceof DOMException && err.name === "QuotaExceededError";
      ftLog("terminal", {
        transferId,
        role: "sender",
        outcome: "failed",
        reason: isQuota ? "quota" : ((err as Error)?.name ?? "unknown"),
      });
      await deleteAllSources();
      notify({
        stage: "failed",
        reason: "storage-full",
        errorCode: "STORAGE_FULL",
        failStage: "preflight",
      });
      throw new InsufficientStorageError();
    }

    builtFiles.push({
      fileId: randomUUID(),
      name: file.name,
      mime: file.type || "application/octet-stream",
      size,
      chunkCount,
      sha256: bytesToHex(deps.hash.final(hashState)),
    });
  }

  // Authoritative partCount/plan — recomputed from the REAL FileEntry list (real fileIds),
  // per the multi-file design's `partCount = partPlan(files).length`. Numerically identical
  // to `precheckPartCount` above (same chunkCounts), so this can never newly exceed
  // FT_MAX_PART_COUNT after already passing the preflight check.
  const plan = partPlan(builtFiles);
  const partCount = plan.length;

  const manifest: TransferManifest = {
    v: FT_PROTOCOL_VERSION,
    transferId,
    senderDeviceId: deps.senderDeviceId,
    recipientDeviceIds: [recipientDeviceId],
    files: builtFiles,
    totalBytes,
    chunkSize: CHUNK_SIZE,
    cipher: "xchacha20poly1305-chunked-v1",
    noncePrefix: encodeBase64(noncePrefixBytes),
    createdAt: now(),
  };

  const invalidReason = validateManifest(manifest);
  if (invalidReason) {
    // Belt+braces per the execution plan — should be unreachable if the
    // manifest above is built correctly.
    await deleteAllSources();
    throw new InvalidManifestError(invalidReason);
  }

  // Sent BEFORE awaitAccept registers its pending-map entry ("send before
  // listen", not "listen before send"). Safe by network-latency physics, not
  // by luck: an accept/decline requires a full FCM round-trip to Android plus
  // a human tap, which dwarfs the synchronous registration that happens the
  // instant `awaitAccept` is invoked below — there's no realistic window for
  // a reply to arrive first. Reordering would also invert the "offer lifecycle"
  // test's invocation-order contract (sendCtrl before awaitAccept) for no
  // practical benefit, so it's left as-is (PR #705 review, N4).
  await deps.sendCtrl({ kind: "offer", manifest });

  // #808 item 6 — start in "delivering"; the recipient's offer_received ack (if
  // it arrives before the decision) advances the card to "waiting-accept".
  notify({ stage: "delivering", transferId });
  let decision: AcceptDecision;
  // Foreground-tier recipients cannot be woken, so the 5-minute window — sized for a push that
  // lands in seconds — would expire before the phone is even picked up. Spec D5.
  const needsAppOpen = deps.targetPushCapable === false;
  const acceptWindowMs = needsAppOpen ? FOREGROUND_ACCEPT_TIMEOUT_MS : ACCEPT_TIMEOUT_MS;
  try {
    decision = await deps.awaitAccept(transferId, acceptWindowMs, () =>
      notify({ stage: "waiting-accept", transferId, needsAppOpen })
    );
  } catch {
    // Reached from BOTH windows: 5 min for a push-capable recipient, 30 min for a
    // foreground-tier one. The gap below narrows to 2x on the relaxed path, but the
    // cancel is just as necessary — a stale Accept button is stale at either width.
    //
    // #834: the sender's ACCEPT_TIMEOUT_MS (5 min) is 12x shorter than the
    // receiver's 1h offer TTL (`FT_OFFER_TTL_MS`, a client constant distinct from
    // the server's 12h record), so for ~55 min the receiver would still show a
    // live Accept button for an offer this sender has already abandoned — and
    // tapping it dies with a bare EXPIRED, the peer never learning it was
    // answered. Announce the timeout so the receiver's existing cancel handling
    // drops the stale offer (writes its "cancelled" tombstone). `cancel.reason`
    // is a free-form string — no new wire code. Best-effort: a failed send just
    // falls back to the receiver's own TTL, no worse than before.
    await deps.sendCtrl({ kind: "cancel", transferId, reason: "no-answer" }).catch(() => {});
    // W2 window (offer -> accept): no record exists yet — nothing to
    // resume; just clean up the OPFS orphan(s) (also covered by the M2
    // offscreen-host startup GC once TASK-17 ships).
    await deleteAllSources();
    notify({ stage: "failed", reason: "no-answer", failStage: "offer" });
    throw new OfferNoAnswerError();
  }
  if (decision.decision === "decline") {
    await deleteAllSources();
    notify({ stage: "failed", reason: "rejected", failStage: "offer" });
    throw new OfferDeclinedError();
  }

  // Multi-file ignores `resumeFromChunk` this slice — a re-PUT is idempotent (ciphertext is
  // deterministic, crypto-spec §1.2), so this only costs some redundant re-upload rather than
  // correctness. TODO(multi-file resume-skip): extend clamping to a per-file/global-part
  // mapping once a receiver can express a multi-file resume offset.
  const skipBelowPart =
    files.length === 1 ? clampSkipBelowPart(decision.resumeFromChunk, builtFiles[0].chunkCount) : 0;
  const cipherBytes = builtFiles.reduce((sum, f) => sum + f.chunkCount * AEAD_OVERHEAD + f.size, 0);

  let createResp: { expiresAt: number };
  try {
    createResp = await deps.api.createTransfer({
      transferId,
      recipientDeviceId,
      partCount,
      cipherBytes,
    });
  } catch (err) {
    // Create-time rejection (TASK-30) — no P-S1 record exists yet, so nothing to resume; clean up
    // the OPFS orphan(s) and report a code-specific reason so the UI can show "daily limit reached"
    // rather than a generic failure. Every OTHER create-time rejection
    // (`concurrent_limit_exceeded`/`file_too_large`/`not_paired_with_target`/network) falls back to
    // the generic INTERNAL bucket — see `errors.ts`'s `isQuotaExceeded` doc.
    await deleteAllSources();
    notify({
      stage: "failed",
      reason: "upload-failed",
      transferId,
      errorCode: isQuotaExceeded(err) ? "QUOTA_EXCEEDED" : "INTERNAL",
      failStage: "transfer",
    });
    throw err;
  }

  // P-S1 (docs/file-transfer-resume.md §4.1) — full record, BEFORE any part
  // upload. `record` is mutated in place and re-persisted at each
  // subsequent write point (P-S2/P-S4) — a plain local variable is fine
  // since a fresh send only ever runs one upload loop at a time.
  const record: FtSenderStateRecord = {
    v: 1,
    transferId,
    peerDeviceId: recipientDeviceId,
    peerPub: encodeBase64(deps.recipientPublicKey),
    manifestJson: JSON.stringify(manifest),
    partCount,
    partsUploaded: [],
    skipBelowPart,
    status: "transferring",
    expiresAt: createResp.expiresAt,
    createdAt: now(),
    updatedAt: now(),
    opfsPath: `ft/send-${transferId}`,
  };
  await deps.state.save(record);

  // §3 eligibility gate (docs/file-transfer-p2p.md) — P2P is attempted only on the initial live
  // session of a fresh accept with `skipBelowPart === 0` (the `resumeFromChunk` gate), AND only
  // for a single-file transfer this slice (multi-file transfer design §8a — the P2P chunk loop
  // still speaks single-file global chunk indexing; see the module doc). `sendFiles` IS ALWAYS
  // that fresh/initial-live-session path, so this structurally satisfies "P2P only on the initial
  // live session" (§1.6) — `SenderResumeDeps` never gains a `p2p` field, which is what makes
  // "never re-promote" hold STRUCTURALLY: only this fresh path can ever attempt P2P.
  const p2pEligible =
    deps.p2p !== undefined && FT_P2P_ENABLED && skipBelowPart === 0 && files.length === 1;
  // Acquire the SAME Web Lock `senderResume.ts`'s resume algorithm uses, right after P-S1, BEFORE
  // attempting P2P — held for the whole P2P-attempt-then-fallback window (released in the same
  // `finally` as `deps.control?.deregister` below, i.e. for the REST of this function's
  // execution). This closes, for free, the pre-existing race where a panel-mount
  // `resumePendingSendsOnPanelMount()` scan could otherwise find this freshly-P-S1'd
  // "transferring" record and race a live fresh send (§16 pt.4).
  const p2pLockHeld = p2pEligible && deps.p2p ? await deps.p2p.acquireLock(transferId) : false;

  const uploadSet: number[] = [];
  for (let p = skipBelowPart; p < partCount; p++) uploadSet.push(p);

  // Register the ACTIVE control seam only once the record is durably
  // "transferring" (P-S1 just ran above) — this is the window §8.2's
  // "Active record" ctrl-table column covers; a pause/cancel/progress ctrl
  // arriving before this point (still inside offer/accept negotiation) has
  // no record to act on and is correctly dropped by the caller
  // (senderDeps.ext.ts's notifyFileCtrlReceived).
  deps.control?.register(transferId, (chunksDone) =>
    notify({ stage: "peer-progress", transferId, chunksDone })
  );
  try {
    // Set when the upload loop hits §4.4 ALREADY_COMPLETE — the completion
    // tail then skips the redundant POST /complete.
    let serverAlreadyComplete = false;
    // Resolved outcome of a P2P attempt, if one ran — "not-attempted" (the default) means "no
    // deps.p2p, ineligible, or connect() itself returned null (T1)": fall through to the
    // UNCHANGED M2 upload loop below, byte-identical to every existing test's expectations.
    let p2pOutcome: P2pSendOutcome = { kind: "not-attempted" };
    // Highest part boundary already notified during a P2P attempt (onChunkProgress throttle) —
    // starts at 0 because onTransferring already emits the partIndex:0 event at channel open.
    let lastP2pProgressPart = 0;

    try {
      if (p2pLockHeld && deps.p2p) {
        const p2p = deps.p2p;
        // `p2pEligible` above already requires `files.length === 1` — sources[0]/builtFiles[0]
        // are this transfer's only file (multi-file P2P is a later per-surface slice).
        const p2pSource = sources[0];
        const p2pFileId = builtFiles[0].fileId;
        const p2pChunkCount = builtFiles[0].chunkCount;
        p2pOutcome = await p2p.attempt({
          transferId,
          fileId: p2pFileId,
          chunkCount: p2pChunkCount,
          source: p2pSource,
          crypto: deps.crypto,
          transferKey,
          noncePrefix: noncePrefixBytes,
          // §10.1/§10.2/§11.1 pause/cancel rows: the SAME control seam the M2 upload loop below
          // already polls — `p2pSenderAttempt.ts` throws `UploadPausedError`/`UploadCancelledError`
          // on detecting either, which this SAME try/catch (below) already handles for the relay
          // path, so both transports reuse the identical P-S3/P-S4 terminal logic.
          checkInterrupt: () => deps.control?.checkInterrupt(transferId),
          // TASK-30 — the "direct" transport badge: one synthetic uploading event the instant the
          // data channel opens (TRANSFERRING_P2P). `partIndex: 0` is informational/cosmetic only
          // (mirrors `p2pSenderAttempt.ts`'s own `-1` convention for "no relay part index applies
          // here") — no caller reads it programmatically; the UI shows the badge off `transport`
          // alone.
          onTransferring: () =>
            notify({ stage: "uploading", partIndex: 0, partCount, transferId, transport: "p2p" }),
          // Hardware-demo follow-up: tick the card's part counter during P2P too. The driver
          // fires per chunk; throttle to part boundaries here so the UI re-renders ~once per
          // part instead of thousands of times per transfer.
          onChunkProgress: (chunksSent) => {
            const part = Math.min(partCount - 1, Math.floor(chunksSent / CHUNKS_PER_PART));
            if (part > lastP2pProgressPart) {
              lastP2pProgressPart = part;
              notify({
                stage: "uploading",
                partIndex: part,
                partCount,
                transferId,
                transport: "p2p",
              });
            }
          },
        });

        if (p2pOutcome.kind === "not-attempted" && p2pOutcome.reason === "connect_failed") {
          // A connect that never opened is NOT a demote in the protocol sense (no
          // channel, so no demote ctrl) — but downstream it is the same question:
          // "why did this end up on the relay?" Without it, a P2P peer that can
          // never connect is indistinguishable from one that has no P2P at all.
          try {
            deps.p2p?.onDemote?.({
              trigger: "connect_failed",
              lastVerifiedChunk: 0,
              chunkCount: p2pChunkCount,
            });
          } catch {
            // never let observability break a transfer
          }
        }

        if (p2pOutcome.kind === "demoted") {
          // Analytics seam (#808 follow-up) — exactly once per attempt, fail-open:
          // a throw here would abort a relay fallback that is about to succeed.
          try {
            deps.p2p?.onDemote?.({
              trigger: p2pOutcome.trigger,
              lastVerifiedChunk: p2pOutcome.lastVerifiedChunk,
              chunkCount: p2pChunkCount,
            });
          } catch {
            // never let observability break a transfer
          }
          ftLog("relay_fallback", {
            transferId,
            role: "sender",
            acked: p2pOutcome.lastVerifiedChunk,
            total: p2pChunkCount,
          });
          // §8.3 step 3's pinned demote mapping.
          const newSkip = mapDemoteToSkipBelowPart(
            p2pOutcome.lastVerifiedChunk,
            p2pChunkCount,
            partCount,
            CHUNKS_PER_PART
          );
          record.skipBelowPart = Math.max(record.skipBelowPart, newSkip);
          record.updatedAt = now();
          await deps.state.save(record); // P-S5

          // §9.3 demotedFromP2p (in-memory only, §1.7, session-scoped — this subscription exists
          // ONLY for the duration of this reconcile call, never persisted; a crash/restart's plain
          // resume hits the M2 UI-only complete-ctrl row unchanged, §14 W-P4/W-P9). If the wire
          // `complete{verified:true}` ctrl arrives WHILE the post-demotion relay reconcile below is
          // still running, abort it early via the SAME pause-interrupt the relay loop's per-part
          // checkpoint already polls, then override the resulting "paused" persist with "complete"
          // — write-ordering makes this safe: `reconcileSenderAndContinue` is fully awaited BEFORE
          // this override runs, so the loop's own persist happens-before it, and the terminal
          // "complete" write is strictly last.
          let earlyComplete = false;
          const unsubscribeEarlyComplete = p2p.subscribeWireComplete(transferId, () => {
            earlyComplete = true;
            p2p.requestPause(transferId); // best-effort — a brief pre-registration window can miss this; accepted (bounded waste, mirrors §14's W-P4)
          });
          try {
            // §5.2 core, called DIRECTLY (never `resumeSenderTransfer`, which would re-acquire the
            // ALREADY-HELD lock with `{ ifAvailable: true }` and skip — a guaranteed bug). This owns
            // the ENTIRE rest of the transfer's lifecycle, including its own terminal persist/UI
            // events via `onProgress` — `sendFiles` must not fall through to its own tail below.
            await reconcileSenderAndContinue(record, p2p.resumeDeps, true);
          } finally {
            unsubscribeEarlyComplete();
          }
          if (earlyComplete && record.status === "paused") {
            await p2p.resumeDeps.api.deleteTransfer(transferId).catch(() => {});
            // Still the post-demotion branch: this override only fires when the peer's
            // wire complete raced the relay reconcile we started BECAUSE of a demote.
            // Reporting plain "relay" here would hide the demote on exactly the
            // transfers where P2P failed but the relay rescued it — the case the
            // transport split exists to make visible.
            await persistTerminalSender(
              record,
              "complete",
              undefined,
              p2p.resumeDeps,
              "relay_after_demote"
            );
          }
          return;
        }
        // "completed": fall through — treated EXACTLY like reaching the end of `runUploadLoop`
        // succeeding (the completion tail below), since `p2pOutcome.kind !== "completed"` gates the
        // upload loop call itself. "not-attempted": same fallthrough, but the gate below is false,
        // so the UNCHANGED upload loop runs.
      }

      if (p2pOutcome.kind !== "completed") {
        await runUploadLoop(uploadSet, {
          api: deps.api,
          crypto: deps.crypto,
          plan,
          sourceFor: (fileIndex) => sources[fileIndex],
          fileChunkCounts: builtFiles.map((f) => f.chunkCount),
          transferKey,
          noncePrefix: noncePrefixBytes,
          transferId,
          partCount,
          onPartUploaded: async (partIndex) => {
            record.partsUploaded = [...record.partsUploaded, partIndex].sort((a, b) => a - b);
            record.updatedAt = now();
            await deps.state.save(record); // P-S2
          },
          onProgress: (partIndex, totalParts) =>
            notify({ stage: "uploading", partIndex, partCount: totalParts, transferId }),
          checkInterrupt: () => deps.control?.checkInterrupt(transferId),
        });
      }
    } catch (err) {
      if (err instanceof UploadPausedError) {
        // Checkpoint-resolved P-S3: the loop already stopped cleanly after a
        // completed part's P-S2 persist — no in-flight bytes to abort, no
        // ctrl, no DELETE, OPFS copy kept for the eventual resume.
        record.status = "paused";
        record.updatedAt = now();
        await deps.state.save(record); // P-S3
        notify({ stage: "paused", transferId });
        return;
      }
      if (err instanceof UploadCancelledError) {
        // Only a LOCAL cancel echoes an outbound ctrl — a peer-initiated
        // cancel means the peer already knows (§8.2).
        if (deps.control?.getCancelOrigin(transferId) === "local") {
          await deps.sendCtrl({ kind: "cancel", transferId, reason: "user" }).catch(() => {});
        }
        await deps.api.deleteTransfer(transferId).catch(() => {});
        record.status = "cancelled";
        record.updatedAt = now();
        await deps.state.save(record).catch(() => {}); // P-S4
        await deleteAllSources();
        notify({ stage: "failed", reason: "cancelled", transferId, failStage: "transfer" });
        throw err;
      }
      if (err instanceof UploadPeerFailedError) {
        // §8.2 `error` row: the RECEIVER reported a terminal failure while this
        // loop was live. No echoed ctrl (it told us) and no DELETE (it already
        // deleted for HASH_MISMATCH/CHUNK_AUTH_FAIL/STORAGE_FULL; a second one
        // would just 404). Terminal "failed" carries the PEER's code — before
        // this branch existed the loop ran to completion and reported `done`,
        // telling the user a file arrived that the receiver had thrown away.
        record.status = "failed";
        // Already an FtErrorCode — isFileCtrl normalized it at the wire boundary.
        // Kept as a narrowed local for `notify`; the record field is a widened `string`.
        const peerErrorCode: FtErrorCode = deps.control?.getPeerErrorCode(transferId) ?? "INTERNAL";
        record.errorCode = peerErrorCode;
        record.updatedAt = now();
        await deps.state.save(record).catch(() => {}); // P-S4
        await deleteAllSources();
        notify({
          stage: "failed",
          reason: "upload-failed",
          transferId,
          errorCode: peerErrorCode,
          // HASH_MISMATCH is the receiver's whole-file verification failing — this
          // branch (#841) is the ONLY way the sender ever learns about it, and it is
          // exactly the case `"verify"` exists for. Every other peer code
          // (CHUNK_AUTH_FAIL / STORAGE_FULL / ...) killed the transfer while bytes
          // were still moving, which is "transfer".
          failStage: peerErrorCode === "HASH_MISMATCH" ? "verify" : "transfer",
        });
        throw err;
      }
      if (isAlreadyComplete(err)) {
        // §4.4 ALREADY_COMPLETE mid-fresh-send: another context (a parallel
        // resume pass racing this session) already ran POST /complete for this
        // transfer. Not a failure — fall through to the completion tail below,
        // skipping the now-redundant POST /complete. (No re-GET here, unlike
        // the resume path's §5.2 step 5: the fresh-send seam's TransferApi has
        // no getTransfer by design, and ALREADY_COMPLETE is definitionally
        // "server status is complete", so the tail is the step-2 complete
        // branch's exact behavior.)
        serverAlreadyComplete = true;
      } else if (isDefinitive404(err)) {
        // §7.2 EXPIRED path: the transfer vanished server-side mid-upload
        // (TTL / cancel / cleanup-worker race). Terminal "expired" — NOT
        // "failed"/INTERNAL — with the §7.2 ordering: persist terminal, clean
        // artifacts, best-effort error{EXPIRED} ctrl. No DELETE call: the
        // transfer is already gone (a courtesy DELETE would just 404).
        record.status = "expired";
        record.errorCode = "EXPIRED";
        record.updatedAt = now();
        await deps.state.save(record).catch(() => {}); // P-S4 (best-effort)
        await deleteAllSources();
        await deps
          .sendCtrl({ kind: "error", transferId, code: "EXPIRED", message: "" })
          .catch(() => {});
        notify({
          stage: "failed",
          reason: "upload-failed",
          transferId,
          errorCode: "EXPIRED",
          failStage: "transfer",
        });
        throw err;
      } else {
        // Terminal "failed"/INTERNAL — exhausted retries or a local
        // OPFS/crypto failure. Both cleanup steps are best-effort and
        // independently guarded: if sendCtrl itself throws (e.g. a transient
        // not_paired), deleteTransfer must still run, and the ORIGINAL upload
        // error (`err`) — not a secondary cleanup failure — is what surfaces
        // to the caller/UI.
        await deps
          .sendCtrl({
            kind: "error",
            transferId,
            code: "INTERNAL",
            message: "part upload failed after retries",
          })
          .catch(() => {});
        await deps.api.deleteTransfer(transferId).catch(() => {});
        record.status = "failed";
        record.errorCode = "INTERNAL";
        record.updatedAt = now();
        await deps.state.save(record).catch(() => {}); // P-S4 (best-effort)
        await deleteAllSources();
        notify({
          stage: "failed",
          reason: "upload-failed",
          transferId,
          errorCode: "INTERNAL",
          failStage: "transfer",
        });
        throw err;
      }
    }

    try {
      if (!serverAlreadyComplete) {
        if (p2pOutcome.kind === "completed") {
          // §10.3: a pure-P2P completion is unaffected by relay-record expiry — no server call ever
          // happened in a P2P state, so swallow a DEFINITIVE_404 (or any failure) from
          // `POST /complete` here; the receiver has already verified the whole file.
          await deps.api.complete(transferId).catch(() => {});
        } else {
          await deps.api.complete(transferId);
        }
      }
      await deps.sendCtrl({ kind: "complete", transferId, verified: true });
    } catch (err) {
      // Mirrors the part-exhaustion catch above: both cleanup steps are
      // best-effort and independently guarded, and the ORIGINAL error — not a
      // secondary cleanup failure — is what surfaces to the caller/UI. The
      // record is deliberately NOT marked complete here, so a resume sees the
      // true (incomplete) state.
      await deps
        .sendCtrl({
          kind: "error",
          transferId,
          code: "INTERNAL",
          message: "complete step failed",
        })
        .catch(() => {});
      await deps.api.deleteTransfer(transferId).catch(() => {});
      // Same INTERNAL code the peer-facing error ctrl above carries — the two
      // sibling upload-failed sites attach an errorCode, this one must too or
      // the UI falls back to the retry-exhaustion copy for a finalize failure.
      notify({
        stage: "failed",
        reason: "upload-failed",
        transferId,
        errorCode: "INTERNAL",
        failStage: "finalize",
      });
      throw err;
    }
    record.status = "complete";
    record.updatedAt = now();
    await deps.state.save(record); // P-S4
    await Promise.all(sources.map((s) => s.deleteCopy()));
    notify({
      stage: "done",
      transferId,
      // The post-demotion path never reaches here — it returns at the
      // reconcileSenderAndContinue handoff above, which owns its own terminal and
      // reports "relay_after_demote". So this site is only ever pure-P2P or pure-relay.
      transport: p2pOutcome.kind === "completed" ? "p2p" : "relay",
    });
  } finally {
    if (p2pLockHeld && deps.p2p) await deps.p2p.releaseLock(transferId);
    deps.control?.deregister(transferId);
  }
}
